import { useQuery } from "@tanstack/react-query";
import { Check, MessageSquare, Play, Star } from "lucide-react";
import type { ReactNode } from "react";

import { fetchCommentCounts } from "../lib/media/comments";
import { fetchFavoriteIds } from "../lib/media/favorites";
import { formatDuration } from "../lib/media/format";
import { queryKeys } from "../lib/queryKeys";

interface Props {
  thumbUrl: string | undefined;
  /** 期限切れなどで表示できなかったときに呼ぶ(呼び出し側で取り直す) */
  onThumbError: () => void;
  onClick: () => void;
  /** 選択の丸を出すか。選択モード中に true(ゴミ箱は常時) */
  showCheck: boolean;
  isSelected: boolean;
  /** 選択中でなくても薄く出す(ゴミ箱の「削除済み」の印) */
  dimmed?: boolean;
  /** 動画バッジや残り日数など、タイルに重ねる表示 */
  children?: ReactNode;
}

/** 一覧グリッドの1マス。タイムライン・お気に入り・ゴミ箱で共通 */
export default function MediaTile({
  thumbUrl,
  onThumbError,
  onClick,
  showCheck,
  isSelected,
  dimmed = false,
  children,
}: Props) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative aspect-square overflow-hidden rounded-md bg-[var(--color-line)]"
    >
      {thumbUrl !== undefined && (
        <img
          src={thumbUrl}
          alt=""
          loading="lazy"
          decoding="async"
          // マウスを載せると気持ち寄る。押せることが手触りで伝わる
          className={`size-full object-cover transition-transform duration-300 group-hover:scale-105 ${
            isSelected ? "opacity-60" : dimmed ? "opacity-80" : ""
          }`}
          onError={onThumbError}
        />
      )}
      {children}
      {showCheck && (
        <span
          className={`absolute top-1.5 right-1.5 flex size-5 items-center justify-center rounded-full ${
            isSelected
              ? "bg-[var(--color-accent)]"
              : "border border-[var(--color-line)] bg-white/85"
          }`}
        >
          {isSelected && <Check className="size-3 text-white" aria-hidden />}
        </span>
      )}
    </button>
  );
}

/**
 * タイル左下のコメント数。再生時間と同じ体裁で出す。
 * 一覧のどのタイルも同じキャッシュを見るだけなので、各タイルで引いてよい。
 */
export function TileMarks({ mediaId }: { mediaId: string }) {
  const counts = useQuery({ queryKey: queryKeys.commentCounts, queryFn: fetchCommentCounts });
  const count = counts.data?.get(mediaId) ?? 0;
  if (count === 0) return null;
  return (
    <span className="absolute bottom-1.5 left-1.5 flex items-center gap-0.5 rounded bg-black/55 px-1.5 py-0.5 text-[10px] text-white">
      <MessageSquare className="size-2.5 fill-current" aria-hidden />
      {count > 99 ? "99+" : count}
    </span>
  );
}

/** タイル左上のお気に入りの印(選択の丸の反対側)。アルバム一覧で出す */
export function TileFavoriteStar({ mediaId }: { mediaId: string }) {
  const favorites = useQuery({ queryKey: queryKeys.favorites, queryFn: fetchFavoriteIds });
  if (!(favorites.data?.has(mediaId) ?? false)) return null;
  return (
    <Star
      className="absolute top-1.5 left-1.5 size-4 fill-[var(--color-accent)] text-[var(--color-accent)] drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]"
      aria-hidden
    />
  );
}

/** タイル右下の動画バッジ。長さが分からないときはアイコンだけ出す */
export function VideoBadge({ durationSeconds }: { durationSeconds: number | null }) {
  return (
    <span className="absolute right-1.5 bottom-1.5 flex items-center gap-1 rounded bg-black/55 px-1.5 py-0.5 text-[10px] text-white">
      <Play className="size-2.5 fill-current" aria-hidden />
      {durationSeconds === null ? "" : formatDuration(durationSeconds)}
    </span>
  );
}
